import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import BlogPostsSection from '../components/BlogPostsSection';
import BottomBanner from '../components/BottomBanner';
import FeaturedEvents from '../components/FeaturedEvents';
import FeatureBlock from '../components/FeatureBlock';
import Hero from '../components/Hero';
import Layout from '../components/Layout';
import MailingListSignup from '../components/MailingListSignup';
import NewsSlider from '../components/NewsSlider';
import SEO from '../components/Seo';
import SocialLinks from '../components/SocialLinks';
import Spacer from '../components/common/Spacer';
import { extractContent } from './../util';
config.autoAddCss = false;

// Deep link target for the mailing list signup, e.g. `/#mailing-list` or `/?signup`.
const MAILING_LIST_SECTION_ID = 'mailing-list';
// Fallback height for the sticky navbar so the section isn't hidden behind it.
const DEFAULT_SCROLL_OFFSET_PX = 96;

const getNavbarOffset = () => {
  const nav = document.querySelector('nav[role="navigation"]');
  return nav ? nav.getBoundingClientRect().height : DEFAULT_SCROLL_OFFSET_PX;
};

const shouldScrollToMailingList = () => {
  if (typeof window === 'undefined') return false;
  const { hash, search } = window.location;
  return (
    hash === `#${MAILING_LIST_SECTION_ID}` ||
    new URLSearchParams(search).has('signup')
  );
};

const scrollToMailingList = (behavior: ScrollBehavior) => {
  const el = document.getElementById(MAILING_LIST_SECTION_ID);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.pageYOffset;
  window.scrollTo({ top: top - getNavbarOffset(), behavior });
};

const useScrollToMailingList = () => {
  React.useEffect(() => {
    if (!shouldScrollToMailingList()) return;

    // Scroll right away, then again after images/lazy content above the
    // section have had a chance to load and shift the layout.
    scrollToMailingList('auto');
    const timers = [400, 1200].map(ms =>
      window.setTimeout(() => scrollToMailingList('smooth'), ms)
    );

    return () => timers.forEach(window.clearTimeout);
  }, []);
};

const IndexPage = () => {
  const data = useStaticQuery(graphql`
    query {
      allContentfulHomePage(
        filter: { node_locale: { eq: "en-US" } }
        limit: 1
      ) {
        edges {
          node {
            heroHeader
            heroSubHeader
            heroCtaButtonText
            heroCtaButtonLink
            heroImages {
              fluid(maxWidth: 1600) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            featureBlock1Title
            featureBlock1Body
            featureBlock1CtaButtonText
            featureBlock1CtaButtonLink
            featureBlock1Position
            featureBlock1Images {
              fluid(maxWidth: 1200) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            featureBlock2Title
            featureBlock2Body
            featureBlock2CtaButtonText
            featureBlock2CtaButtonLink
            featureBlock2Position
            featureBlock2Images {
              fluid(maxWidth: 1200) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            featureBlock3Title
            featureBlock3Body
            featureBlock3CtaButtonText
            featureBlock3CtaButtonLink
            featureBlock3Position
            featureBlock3Images {
              fluid(maxWidth: 1200) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            featuredEvent1 {
              id
              title
              startDate
              endDate
              slug
              image {
                fluid(maxWidth: 1200) {
                  ...GatsbyContentfulFluid_withWebp
                }
              }
            }
            featuredEvent2 {
              id
              title
              startDate
              endDate
              slug
              image {
                fluid(maxWidth: 1200) {
                  ...GatsbyContentfulFluid_withWebp
                }
              }
            }
            featuredEvent3 {
              id
              title
              startDate
              endDate
              slug
              image {
                fluid(maxWidth: 1200) {
                  ...GatsbyContentfulFluid_withWebp
                }
              }
            }
          }
        }
      }
      allContentfulEvent(
        filter: { node_locale: { eq: "en-US" } }
        sort: { fields: startDate, order: ASC }
      ) {
        edges {
          node {
            id
            title
            startDate
            endDate
            description {
              description
            }
            slug
            price
            image {
              fluid(maxWidth: 600) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
          }
        }
      }
      allContentfulResource(
        limit: 12
        filter: { showOnHomepage: { eq: true }, node_locale: { eq: "en-US" } }
        sort: { fields: homepageOrder, order: ASC }
      ) {
        edges {
          node {
            title
            body {
              body
            }
            image {
              fluid {
                src
              }
            }
            type
            slug
            featuredResources {
              title
            }
          }
        }
      }
      # allContentfulNewsItem(
      #   filter: { showOnHomepage: { eq: true }, node_locale: { eq: "en-US" } }
      # ) {
      #   edges {
      #     node {
      #       title
      #       slug
      #     }
      #   }
      # }
      blogPostsForSlider: allContentfulBlogPost(
        filter: { showOnHomepage: { eq: true }, node_locale: { eq: "en-US" } }
      ) {
        edges {
          node {
            title
            slug
            featuredImage {
              sizes {
                src
              }
            }
          }
        }
      }
      allContentfulBlogPost(
        filter: { node_locale: { eq: "en-US" } }
        sort: { fields: publishedDate, order: DESC }
        limit: 3
      ) {
        edges {
          node {
            id
            featuredImage {
              fluid {
                src
              }
            }
            title
            publishedDate
            createdAt
            slug
            author {
              firstName
              lastName
              image {
                fluid {
                  src
                }
              }
            }
          }
        }
      }
      blogFallbackImage: file(
        relativePath: { eq: "images/news-fallback.png" }
      ) {
        childImageSharp {
          sizes(maxWidth: 600) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      card1Image: file(relativePath: { eq: "images/info-card-1.png" }) {
        childImageSharp {
          sizes(maxWidth: 600) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      card2Image: file(relativePath: { eq: "images/info-card-2.png" }) {
        childImageSharp {
          sizes(maxWidth: 600) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      card3Image: file(relativePath: { eq: "images/info-card-3.png" }) {
        childImageSharp {
          sizes(maxWidth: 600) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      facebookGroupsImage: file(
        relativePath: { eq: "images/classroom-bg.jpg" }
      ) {
        childImageSharp {
          sizes(maxWidth: 300) {
            ...GatsbyImageSharpSizes
          }
        }
      }
      bgImage: file(relativePath: { eq: "images/teachers-college-2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      logo: file(relativePath: { eq: "images/logo-white.png" }) {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  const homePageData = data.allContentfulHomePage?.edges?.[0]?.node || null;

  // Get featured events from Contentful
  const primaryEvent = homePageData?.featuredEvent1 || null;
  const secondaryEvents = [
    homePageData?.featuredEvent2,
    homePageData?.featuredEvent3
  ].filter(Boolean);

  const blogPostsForSlider = extractContent(data.blogPostsForSlider);
  // const newsItems = extractContent(data.allContentfulNewsItem);
  const blogPosts = extractContent(data.allContentfulBlogPost);

  const [showBanner, setShowBanner] = React.useState(false);

  useScrollToMailingList();

  return (
    <Layout>
      <SEO title="Home" />
      <Hero homePageData={homePageData} />
      {homePageData?.featureBlock1Title || homePageData?.featureBlock1Body ? (
        <FeatureBlock
          title={homePageData.featureBlock1Title}
          body={homePageData.featureBlock1Body}
          ctaButtonText={homePageData.featureBlock1CtaButtonText}
          ctaButtonLink={homePageData.featureBlock1CtaButtonLink}
          position={homePageData.featureBlock1Position}
          images={homePageData.featureBlock1Images}
          index={0}
          blockNumber={1}
        />
      ) : null}
      {homePageData?.featureBlock2Title || homePageData?.featureBlock2Body ? (
        <FeatureBlock
          title={homePageData.featureBlock2Title}
          body={homePageData.featureBlock2Body}
          ctaButtonText={homePageData.featureBlock2CtaButtonText}
          ctaButtonLink={homePageData.featureBlock2CtaButtonLink}
          position={homePageData.featureBlock2Position}
          images={homePageData.featureBlock2Images}
          index={1}
          blockNumber={2}
        />
      ) : null}
      {homePageData?.featureBlock3Title || homePageData?.featureBlock3Body ? (
        <FeatureBlock
          title={homePageData.featureBlock3Title}
          body={homePageData.featureBlock3Body}
          ctaButtonText={homePageData.featureBlock3CtaButtonText}
          ctaButtonLink={homePageData.featureBlock3CtaButtonLink}
          position={homePageData.featureBlock3Position}
          images={homePageData.featureBlock3Images}
          index={2}
          blockNumber={3}
        />
      ) : null}
      <Spacer x={10} y={10}>
        <div className="w-96 sm:w-192 m-auto">
          <NewsSlider newsItems={[]} blogPosts={blogPostsForSlider} />
        </div>
      </Spacer>
      <FeaturedEvents
        primaryEvent={primaryEvent}
        secondaryEvents={secondaryEvents}
      />
      <Spacer x={10} y={20}>
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto text-center mt-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6 text-balance sans-serif">
              The Latest From Our Blog
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mx-auto mb-8 rounded-full"></div>
          </div>
        </div>
        <div className="mt-8">
          <BlogPostsSection
            blogPosts={blogPosts}
            blogFallbackImage={data.blogFallbackImage}
          />
        </div>
      </Spacer>
      <section className="py-20 mt-24 pattern-dots">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6 text-balance sans-serif">
              Connect with us on Social Media
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mx-auto mb-8 rounded-full"></div>
          </div>
        </div>
        <div className="mt-8 mb-12">
          <SocialLinks />
        </div>
      </section>
      <section
        id={MAILING_LIST_SECTION_ID}
        className="py-20 bg-primary"
        style={{ scrollMarginTop: `${DEFAULT_SCROLL_OFFSET_PX}px` }}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <MailingListSignup />
          </div>
        </div>
      </section>
      {showBanner ? (
        <BottomBanner
          onClose={() => setShowBanner(false)}
          body={`Teachers College Reading and Writing Project is excited to transition to Advancing Literacy at Teachers College. We will be continuing our work with schools and educators across the country and are excited to expand and continue learning and growing from the latest research and practices out there. Across the next few months, we will be updating all of our materials to reflect this new name.
          
Join us for our first TC reunion as Advancing Literacy!`}
          image={data.logo}
          link="/events/october-2023-saturday-reunion-virtual"
        />
      ) : null}
    </Layout>
  );
};

export default IndexPage;
